import { useState, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { validatePassword, formatPasswordIssues } from "@/lib/password";

interface ResetPasswordFormProps {
  onBackToLogin: () => void;
  onSubmit: (data: { email: string; resetToken: string; newPassword: string }) => void | Promise<void>;
  isPending?: boolean;
  isSuccess?: boolean;
  errorMessage?: string | null;
  initialEmail?: string;
  initialToken?: string;
}

export function ResetPasswordForm({
  onBackToLogin,
  onSubmit,
  isPending,
  isSuccess,
  errorMessage,
  initialEmail = "",
  initialToken = "",
}: ResetPasswordFormProps) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    email: initialEmail,
    resetToken: initialToken,
    newPassword: "",
  });

  const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);
  const pass = validatePassword(form.newPassword);
  const canSubmit =
    isEmail(form.email) &&
    form.resetToken.trim().length > 0 &&
    pass.valid &&
    !isPending;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onSubmit(form);
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-full bg-brand-accent">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">
          {t("resetPasswordTitle")}
        </h2>
      </div>

      {isSuccess ? (
        <div className="space-y-4">
          <p className="text-sm text-green-600">{t("passwordResetSuccess")}</p>
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 hover:underline"
            >
              {t("backToLogin")}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <p className="text-sm text-muted-foreground">
            {t("resetPasswordDescription")}
          </p>

          <div className="space-y-2">
            <Label htmlFor="rpEmail" className="text-sm font-medium text-foreground">
              {t("email")}
            </Label>
            <Input
              id="rpEmail"
              type="email"
              placeholder={t("emailPlaceholder")}
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              required
              className="h-11 px-4 py-3 text-[15px] placeholder:text-muted-foreground/70 rounded-xl transition-all duration-200 focus:shadow-lg focus:shadow-primary/10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rpToken" className="text-sm font-medium text-foreground">
              {t("resetToken")}
            </Label>
            <Input
              id="rpToken"
              type="text"
              placeholder={t("resetTokenPlaceholder")}
              value={form.resetToken}
              onChange={(e) => setForm((s) => ({ ...s, resetToken: e.target.value }))}
              required
              className="h-11 px-4 py-3 text-[15px] placeholder:text-muted-foreground/70 rounded-xl transition-all duration-200 focus:shadow-lg focus:shadow-primary/10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rpPassword" className="text-sm font-medium text-foreground">
              {t("newPassword")}
            </Label>
            <Input
              id="rpPassword"
              type="password"
              placeholder={t("newPasswordPlaceholder")}
              value={form.newPassword}
              onChange={(e) => setForm((s) => ({ ...s, newPassword: e.target.value }))}
              required
              className="h-11 px-4 py-3 text-[15px] placeholder:text-muted-foreground/70 rounded-xl transition-all duration-200 focus:shadow-lg focus:shadow-primary/10"
            />
            {!pass.valid && form.newPassword.length > 0 && (
              <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                {formatPasswordIssues(pass.issues).map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}
          </div>

          {errorMessage && (
            <p className="text-xs text-red-600">{errorMessage}</p>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            aria-busy={isPending}
            className="w-full h-12 bg-brand-accent disabled:opacity-60 disabled:cursor-not-allowed hover:bg-brand-accent/90 text-white font-medium text-base transition-all duration-300 hover:shadow-lg hover:shadow-brand-accent/20"
          >
            {isPending ? t("processing") : t("resetPassword")}
          </Button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              {t("backToLogin")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
